import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useVerification } from "../../context/VerificationContext";
import { useExtractAndVerify } from "../../hooks/useExtractAndVerify";
import { useLoadingSteps, type LoadingStep } from "../../hooks/useLoadingSteps";
import type { ExtractInput } from "../../lib/claimsApi.ts";
import type { ApiError } from "../../lib/errors";
import { ROUTES } from "../../route_paths";
import type { OutsideHealthState } from "../NoCoverage";

const STEP_DELAYS = [0, 3000, 6000, 9000, 12000];

interface UseFetchingResult {
  apiError: ApiError | null;
  rateLimitError: { retryAfter: number } | null;
  activeStepIndex: number;
  subMessage: string;
  steps: LoadingStep[];
}

/**
 * Drives the loading screen shown after submission. It calls the combined extract-and-verify
 * endpoint (text or URL) and routes straight to the result or /review for disambiguation.
 */
export function useFetching(): UseFetchingResult {
  const { t } = useTranslation();
  const { rawInput, sourceUrl } = useVerification();
  const navigate = useNavigate();
  const { execute: extractAndVerify } = useExtractAndVerify();

  const [apiError, setApiError] = useState<ApiError | null>(null);
  const [rateLimitError, setRateLimitError] = useState<{ retryAfter: number } | null>(null);

  const steps: LoadingStep[] = useMemo(
    () =>
      (
        t("fetching.steps", { returnObjects: true }) as { label: string; subMessages: string[] }[]
      ).map((s, i) => ({ ...s, delay: STEP_DELAYS[i] })),
    [t]
  );

  const { activeStepIndex, subMessage } = useLoadingSteps(steps);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    if (!rawInput) {
      navigate(ROUTES.landing.path());
      return;
    }

    const input: ExtractInput = sourceUrl ? { source_url: rawInput } : { text: rawInput };
    extractAndVerify(input).then((outcome) => {
      switch (outcome.status) {
        case "rate_limited":
          setRateLimitError({ retryAfter: outcome.retryAfter });
          return;
        case "error":
          setApiError(outcome.error);
          return;
        case "ambiguous":
          navigate(ROUTES.review.path(), { state: { claims: outcome.claims } });
          return;
        case "not_verified": {
          // One claim we do not check has its own page; several go to the review screen, which
          // lists each with its reason.
          const [only] = outcome.claims;
          if (outcome.claims.length === 1) {
            navigate(ROUTES.outsideHealth.path(), {
              state: { claimId: only.id, claimText: only.source_text } satisfies OutsideHealthState,
            });
          } else {
            navigate(ROUTES.review.path(), { state: { claims: outcome.claims } });
          }
          return;
        }
        case "verified": {
          const { claim, verification } = outcome;
          navigate(
            ROUTES.verify.path({
              userEventVerificationId: verification.user_event_verification_id!,
            }),
            {
              state: { result: { ...verification, raw_text: claim.source_text } },
            }
          );
          return;
        }
      }
    });
  }, [extractAndVerify, navigate, rawInput, sourceUrl]);

  return {
    apiError,
    rateLimitError,
    activeStepIndex,
    subMessage,
    steps,
  };
}
